import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DocumentsService from "../../../../common/api/documentsApi";
import ModalButton from "../../../../shared/Ui/Buttons/ModalButtons";
import { EmptyListPlaceholderContent } from "../../../../shared/Ui/EmptyPageBlock";
import Loader from "../../../../shared/Ui/Loader/Loader";
import ModalComponent from "../../../../shared/Ui/ModalComponent/ModalWindow";

type TotalRow = {
  ntinName?: string | null;
  ntin?: string | null;
  kmType?: string | null;
  count?: number | null;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  documentId?: string;
};

const formatKmTypeLabel = (
  kmType: string | undefined | null,
  t: (key: string) => string,
) => {
  if (!kmType) return "-";
  const upper = String(kmType).toUpperCase();
  if (
    upper === "GROUP" ||
    upper.includes("ГРУПП") ||
    upper.includes("ТОПТ")
  ) {
    return t("new_order.km_type_group");
  }
  if (
    upper === "CONSUMER" ||
    upper.includes("ПОТРЕБ") ||
    upper.includes("ТҰТЫН")
  ) {
    return t("new_order.km_type_consumer");
  }
  if (upper === "TRANSPORT" || upper.includes("ТРАНСП")) {
    return t("new_order.km_type_transport");
  }
  return String(kmType);
};

const DocumentTotalsModal = ({ isOpen, onClose, documentId }: Props) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<TotalRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !documentId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    DocumentsService.getDocumentTotals(documentId)
      .then((data) => {
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      })
      .catch((e: any) => {
        if (!cancelled) {
          setRows([]);
          setError(
            e?.response?.data?.message ||
              t("documents_orders.totals_load_error"),
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, documentId, t]);

  const isBackendTotalRow = (row: TotalRow) => {
    const name = String(row?.ntinName || "")
      .trim()
      .toLowerCase();
    return name === "итого" || name === "барлығы" || name === "total";
  };

  const lastRow = rows.length ? rows[rows.length - 1] : null;
  const totalRow =
    lastRow && isBackendTotalRow(lastRow) ? lastRow : null;
  const dataRows = totalRow ? rows.slice(0, -1) : rows;

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      classNameModal="!max-w-[900px] !overflow-x-hidden"
      headerContent={
        <div className="font-semibold text-lg">
          {t("documents_orders.totals_title")}
        </div>
      }
      content={
        <div className="w-full min-w-0">
          {loading ? (
            <div className="py-10">
              <Loader />
            </div>
          ) : error ? (
            <p className="py-6 text-center text-red-600">{error}</p>
          ) : !rows.length ? (
            <EmptyListPlaceholderContent
              title={t("documents_orders.no_data_title")}
              description=""
            />
          ) : (
            <div className="rounded-lg overflow-x-auto bg-white">
              <table className="max-w-full w-full leading-normal">
                <thead className="border-b-2 border-gray-300">
                  <tr>
                    <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                      {t("documents_orders.totals_ntin_name")}
                    </th>
                    <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                      {t("documents_orders.totals_ntin_code")}
                    </th>
                    <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                      {t("documents_orders.package_type")}
                    </th>
                    <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                      {t("documents_orders.totals_count")}
                    </th>
                  </tr>
                </thead>
                <tbody className="font-normal text-center">
                  {dataRows.map((row, index) => (
                    <tr key={`total-${index}`}>
                      <td className="px-5 py-4 text-sm bg-white border-b border-gray-200 text-gray-600">
                        {row.ntinName || ""}
                      </td>
                      <td className="px-5 py-4 text-sm bg-white border-b border-gray-200 text-gray-600">
                        {row.ntin || ""}
                      </td>
                      <td className="px-5 py-4 text-sm bg-white border-b border-gray-200 text-gray-600">
                        {formatKmTypeLabel(row.kmType, t)}
                      </td>
                      <td className="px-5 py-4 text-sm bg-white border-b border-gray-200 text-gray-600">
                        {row.count ?? 0}
                      </td>
                    </tr>
                  ))}
                  {totalRow && (
                    <tr>
                      <td className="px-5 py-4 text-sm bg-white border-b border-gray-200 text-gray-700 font-semibold">
                        {totalRow.ntinName || t("documents_orders.totals_sum")}
                      </td>
                      <td className="px-5 py-4 text-sm bg-white border-b border-gray-200" />
                      <td className="px-5 py-4 text-sm bg-white border-b border-gray-200" />
                      <td className="px-5 py-4 text-sm bg-white border-b border-gray-200 text-gray-700 font-semibold">
                        {totalRow.count ?? 0}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      }
      customContent={
        <ModalButton type="button" color="grey" onClick={onClose}>
          {t("mainButtons.close")}
        </ModalButton>
      }
    />
  );
};

export default DocumentTotalsModal;
